/**
 * Email on the client: the Outbox and history reads, the presses (Send, Cancel, Seen, Resend, Queue
 * reminder), and the dispatcher ping — whenever a signed-in person loads or refocuses the app, it asks the
 * API to send what is due (fire-and-forget; the GitHub schedule does the same every 10 minutes).
 */
import { useQuery } from '@tanstack/react-query';
import type { EmailSettingsView, MessageTab, MessagesResponse, MessageView } from '@shared/email';
import type { InboxResponse } from '@shared/inbox';
import type { MessageProgram } from '@shared/message-programs';
import { apiFetch } from '@/lib/api';

/** 24 Oct 2026 (D-1024-MESSAGES): the program and the search narrow what the server returns. */
export interface MessageNarrow {
  program?: MessageProgram | null;
  q?: string;
}
const narrowQs = (n?: MessageNarrow) => {
  const p = new URLSearchParams();
  if (n?.program) p.set('program', n.program);
  if (n?.q?.trim()) p.set('q', n.q.trim());
  return p.toString();
};

export function useMessages(tab: MessageTab, enabled = true, narrow?: MessageNarrow) {
  const qs = narrowQs(narrow);
  return useQuery({
    queryKey: ['messages', tab, narrow?.program ?? null, narrow?.q?.trim() ?? ''],
    queryFn: () => apiFetch<MessagesResponse>(`/api/messages?tab=${tab}${qs ? `&${qs}` : ''}`),
    enabled,
    refetchInterval: 30_000,
  });
}

/** 23 Oct 2026 (D-1023-INBOX): the scheduling mailbox's replies and bounces that wait for a person. */
export function useInbox(enabled = true, narrow?: MessageNarrow) {
  const qs = narrowQs(narrow);
  return useQuery({
    queryKey: narrow?.program || narrow?.q?.trim() ? ['inbox', narrow?.program ?? null, narrow?.q?.trim() ?? ''] : ['inbox'],
    queryFn: () => apiFetch<InboxResponse>(`/api/inbox${qs ? `?${qs}` : ''}`),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useMessageHistory(q: { tenantId?: string | null; appointmentId?: string | null }, enabled = true) {
  const qs = new URLSearchParams();
  if (q.tenantId) qs.set('tenantId', q.tenantId);
  if (q.appointmentId) qs.set('appointmentId', q.appointmentId);
  return useQuery({
    queryKey: ['message-history', q.tenantId ?? null, q.appointmentId ?? null],
    queryFn: () => apiFetch<{ messages: MessageView[]; now: string }>(`/api/messages/history?${qs.toString()}`),
    enabled: enabled && !!(q.tenantId || q.appointmentId),
  });
}

export function useEmailSettings(enabled = true) {
  return useQuery({ queryKey: ['email-settings'], queryFn: () => apiFetch<EmailSettingsView>('/api/settings/email'), enabled });
}

export const cancelMessage = (id: string) => apiFetch<{ ok: boolean; status: string }>(`/api/messages/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
export const markSeen = (id: string) => apiFetch<{ ok: boolean }>(`/api/messages/${encodeURIComponent(id)}/seen`, { method: 'POST' });
export const resendMessage = (id: string) => apiFetch<{ communicationId: string | null; scheduledAt: string }>(`/api/messages/${encodeURIComponent(id)}/resend`, { method: 'POST' });
export const queueReminders = (dayKey: string) =>
  apiFetch<{ queued: number; skipped: { tenantId: string; why: string }[]; sendAt: string | null; rule: string }>('/api/messages/reminders', { method: 'POST', body: JSON.stringify({ dayKey }) });

let last = 0;
/** Ask the API to send what is due. Never awaited by a screen; at most once a minute from one tab. */
export function pingDispatcher(force = false): void {
  const now = Date.now();
  if (!force && now - last < 60_000) return;
  last = now;
  void fetch('/api/messages/dispatch', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then((r) => r.text())
    .catch(() => undefined);
}

let lastSweep = 0;
/**
 * 1.6.0: ask the app's engine to take what is waiting in the three queues — the same fire-and-forget as the email
 * sender, at most once a minute from one tab. While it answers that more are waiting it is asked again (a long
 * import drains while the app is open). With the flows owning the queue the API answers that it did nothing.
 */
export function pingEngine(force = false): void {
  const now = Date.now();
  if (!force && now - lastSweep < 60_000) return;
  lastSweep = now;
  const once = (left: number): void => {
    void fetch('/api/engine/sweep', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } })
      // a refusal's body is read too (a role the engine is not for — the tech, the crew — gets 403): an unread body keeps
      // the connection open, and the page never goes quiet
      .then((r) => (r.ok ? (r.json() as Promise<{ more?: boolean }>) : r.text().then(() => null)))
      .then((b) => {
        if (b?.more && left > 0) once(left - 1);
      })
      .catch(() => undefined);
  };
  once(30);
}

/** "in 4:32" style remaining time; 0 when due. */
export function secondsLeft(iso: string | null, nowMs: number): number {
  if (!iso) return 0;
  return Math.max(0, Math.round((Date.parse(iso) - nowMs) / 1000));
}

export function formatCountdown(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${m}:${String(r).padStart(2, '0')}`;
}
